// src/socket.js
import { io } from 'socket.io-client';
import tosterService from '../services/toaster.service';

const ENDPOINT = process.env.REACT_APP_SOCKET;
const BASE_APP_URL = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : (process.env.REACT_APP_APP_URL || '');

export let socket = null;

// Persist notification integration and auth across reconnects
let notificationHandlers = null;
let currentUserId = null;
let lastUserAuth = null;

// Keep a registry of listeners we attach so we can re-bind on new socket instances
let registeredListeners = [];
let internalHandlersAttached = false;

/**
 * Check if socket is connected and ready
 * @returns {boolean}
 */
const isSocketReady = () => socket !== null && socket.connected;

/**
 * Set up notification handlers for the socket
 * @param {Object} handlers - Object containing addNotification function and userId
 */
export const setupNotificationHandler = (handlers) => {
    notificationHandlers = handlers;
    if (handlers && handlers.userId) {
        currentUserId = handlers.userId;
    }
};

/**
 * Initialize the socket connection with user data.
 */
export const initiateSocketConnection = (userData) => {
  if (socket && socket.connected) {
    console.log('🔌 Socket already connected');
    return;
  }

  try {
    lastUserAuth = userData || lastUserAuth || {};
    console.log('🔌 Initiating socket connection with user data:', lastUserAuth);
    socket = io(ENDPOINT, {
      auth: { ...lastUserAuth },
      reconnection: true,
      reconnectionAttempts: 50,
      reconnectionDelay: 1000,
    });

    // Re-bind any previously registered listeners
    registeredListeners.forEach(({ event, callback }) => {
      socket.on(event, callback);
    });

    socket.on('connect', () => {
      console.log('🟢 Connected to socket server');
    });

    socket.on('connect_error', (err) => {
      console.error('❌ Connection error:', err);
    });

    socket.on('disconnect', (reason) => {
      console.log('🔴 Disconnected from socket server:', reason);
    });

    socket.on('handshake', (data) => {
      console.log('🤝 Handshake received:', data);
      if (isSocketReady()) {
        socket.emit('handshake_ack', { message: 'pong' });
      }
    });

    socket.on('user_language_updated', (data) => {
      console.log('🔌 User language updated:', data);
    });

    // Attach internal message handlers once per socket instance
    if (!internalHandlersAttached) {
      socket.on('receive_private_message', (data) => {
        // Filter out system messages, whiteboard updates, and messages sent by self
        if ((data.type === 'system') || 
            (data.type === 'whiteboard_update') || 
            (currentUserId && data.userId === currentUserId)) {
          return;
        }

        // Show toast notification when message is received
        tosterService.InfoWithThreadLink(
          `New message from ${data.username || 'Someone'}`,
          `msg-${data.content?.substring(0, 20) || ''}-${Date.now()}`,
          data.threadId,
          `${BASE_APP_URL}/workspace/`,
          data.content
        );

        // Also add to the header notification system if handlers are set up
        if (notificationHandlers && notificationHandlers.addNotification) {
          notificationHandlers.addNotification({
            threadId: data.threadId,
            sender: data.username || 'Someone',
            content: data.content ? data.content.substring(0, 50) : 'New message',
            timestamp: new Date().toISOString(),
            type: 'message'
          });
        }
      });
      internalHandlersAttached = true;
    }
  } catch (error) {
    console.error('❌ Error initializing socket:', error);
  }
};

/**
 * Handle socket disconnection and reconnection attempts
 */
// Deprecated manual reconnection handler removed in favor of Socket.IO built-in reconnection

/**
 * Generic event listener setup with connection check
 * @param {string} event - Event name to listen for
 * @param {Function} callback - Callback function
 * @returns {Function} Cleanup function
 */
const createEventListener = (event, callback) => {
  const registryItem = { event, callback };
  registeredListeners.push(registryItem);

  if (socket) {
    socket.on(event, callback);
  }

  // Cleanup function removes from registry and detaches if socket present
  return () => {
    registeredListeners = registeredListeners.filter(
      (item) => !(item.event === event && item.callback === callback)
    );
    if (socket) {
      socket.off(event, callback);
    }
  };
};

// Update all event listeners to use the new generic function
export const onUserConnected = (callback) =>
    createEventListener('user_connected', callback);

export const onUserDisconnected = (callback) =>
    createEventListener('user_disconnected', callback);

export const onUserListReceived = (callback) =>
    createEventListener('user_list', callback);

// Team member specific events
export const onTeamMembersListReceived = (callback) =>
    createEventListener('team_members_list', callback);

export const onTeamMemberOnline = (callback) =>
    createEventListener('team_member_online', callback);

export const onTeamMemberOffline = (callback) =>
    createEventListener('team_member_offline', callback);

// Request team members list
export const requestTeamMembers = () => {
    if (!isSocketReady()) {
        console.error('❌ Socket not ready for team members request');
        return false;
    }
    try {
        socket.emit('request_team_members');
        console.log('📤 Requested team members list');
        return true;
    } catch (error) {
        console.error('❌ Error requesting team members:', error);
        return false;
    }
};

export const onMessageReceived = (callback) =>
    createEventListener('receive_message', callback);

export const onPrivateMessageReceived = (callback) =>
    createEventListener('receive_private_message', callback);

export const onBroadcastNotificationReceived = (callback) =>
    createEventListener('broadcast_notification', callback);

// Platform notification listener (for admin-sent notifications)
export const onPlatformNotificationReceived = (callback) =>
    createEventListener('platform_notification', callback);

// Update user category for targeted notifications
export const updateUserCategory = (category) => {
    if (!isSocketReady()) {
        console.error('❌ Socket not ready for updating category');
        return false;
    }

    if (!socket) {
        console.error('socket is not init')
        return;
    }
    socket.emit('update_user_category', category);
};

// Correctly spelled version
export const onObserverMessageReceived = (callback) =>
  createEventListener('receive_observer_message', callback);

// Backwards-compatible misspelled export
export const onObserverMessageRecived = (callback) =>
  onObserverMessageReceived(callback);

export const onUserLanguageUpdated = (callback) =>
  createEventListener('user_language_updated', callback);


export const updateUserLanguage = (language) => {
    if (!isSocketReady()) {
        console.error('❌ Socket not ready for sending message');
        return false;
    }

    if (!socket) {
        console.error('socket is not init')
        return;
    }
    socket.emit('update_user_language', language);
}

export const onPrivateMessageToasted = (callback) => {
  // Kept for API compatibility; internal toasts handled centrally in initiate
  return createEventListener('receive_private_message', (data) => {
    if (callback) callback(data);
  });
};
/**
 * Send a private message with connection check
 */
export const sendPrivateMessage = ({
    threadId,
    type,
    content,
    receiverId,
    time,
    fileName,
    fileType,
    fileData,
    senderId,
}) => {
    if (!isSocketReady()) {
        console.error('❌ Socket not ready for sending message');
        return false;
    }

    try {
        console.log('📤 Emitting private message:', {
            threadId,
            type,
            content,
            receiverId,
            time,
            fileName,
            fileType,
            fileData,
            senderId,
        });
        socket.emit('send_private_message', {
            threadId,
            type,
            content,
            receiverId,
            time,
            fileName,
            fileType,
            fileData,
            senderId,
        });
        console.log('✅ Message sent successfully');
        return true;
    } catch (error) {
        console.error('❌ Error sending private message:', error);
        return false;
    }
};

/**
 * Send a call event to the socket server
 */
export const sendCallEvent = ({ callId, senderId, receiverId, time }) => {
    if (!isSocketReady()) {
        console.error('❌ Socket not ready for sending call event');
        return false;
    }
    try {
        console.log('📤 Emitting call event:', {
            callId,
            senderId,
            receiverId,
            time,
        });
        socket.emit('send_call_event', {
            callId,
            senderId,
            receiverId,
            time,
        });
        console.log('✅ Call event sent successfully');
        return true;
    } catch (error) {
        console.error('❌ Error sending call event:', error);
        return false;
    }
};

/**
 * Send a conclude message with connection check
 */
export const sendConcludeMessage = ({ threadId, type, content, receiverId, senderId, time }) => {
    if (!isSocketReady()) {
        console.error('❌ Socket is not connected. Conclude message not sent.');
        return false;
    }

    try {
        socket.emit('send_conclude_message', {
            threadId,
            type,
            content,
            receiverId,
            senderId,
            time
        });
        return true;
    } catch (error) {
        console.error('Error sending conclude message:', error);
        return false;
    }
};

/**
 * Request user list with connection check
 */
export const sendRequestUserList = () => {
    if (!isSocketReady()) {
        console.error('❌ Socket not ready for user list request');
        return false;
    }

    try {
        socket.emit('request_user_list');
        console.log('📤 Requested user list');
        return true;
    } catch (error) {
        console.error('❌ Error requesting user list:', error);
        return false;
    }
};

/**
 * Safely disconnect the socket
 */
export const disconnectSocket = () => {
  internalHandlersAttached = false;
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};